import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface UseSupabaseDataParams {
  url: string;
  refreshInterval?: number; // in minutes
  type?: string; // type of data for caching
  location?: { lat: string | number; lng: string | number; tzid?: string };
}

interface HebrewData {
  title?: string;
  description?: string;
  items?: Array<{
    title: string;
    description: string;
    date?: string;
    type?: string;
    lesson?: string;
    link?: string;
    [key: string]: any;
  }>;
  columns?: Array<{
    key: string;
    label: string;
  }>;
  [key: string]: any;
}

export const useSupabaseData = ({ url, refreshInterval, type = 'default', location }: UseSupabaseDataParams) => {
  const [data, setData] = useState<HebrewData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchFromCache = useCallback(async () => {
    try {
      const { data: cachedData, error: cacheError } = await supabase
        .from('cached_data')
        .select('*')
        .eq('url', url)
        .eq('type', type)
        .single();

      if (cacheError) {
        console.error('Error fetching from cache:', cacheError);
        return null;
      }

      if (!cachedData) {
        return null;
      }

      // Check if cache is still valid (less than 1 hour old)
      const lastUpdated = new Date(cachedData.last_updated);
      const now = new Date();
      const hoursSinceUpdate = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60);

      if (hoursSinceUpdate < 1) {
        return cachedData.data;
      }

      return null;
    } catch (err) {
      console.error('Error in fetchFromCache:', err);
      return null;
    }
  }, [url, type]);

  const updateCache = useCallback(async (newData: HebrewData) => {
    try {
      // First check if an entry exists
      const { data: existingData, error: checkError } = await supabase
        .from('cached_data')
        .select('id')
        .eq('url', url)
        .eq('type', type)
        .single();
      
      // If we found an existing entry, update it
      if (existingData && existingData.id) {
        const { error: updateError } = await supabase
          .from('cached_data')
          .update({
            data: newData,
            last_updated: new Date().toISOString()
          })
          .eq('id', existingData.id);
          
        if (updateError) {
          console.error('Error updating existing cache:', updateError);
        } else {
          console.log('Cache updated successfully');
        }
      } else {
        // If no existing entry, insert a new one
        const { error: insertError } = await supabase
          .from('cached_data')
          .insert({
            url,
            type,
            data: newData,
            last_updated: new Date().toISOString()
          });
          
        if (insertError) {
          console.error('Error inserting new cache:', insertError);
        } else {
          console.log('New cache entry created successfully');
        }
      }
    } catch (err) {
      console.error('Error in updateCache:', err);
    }
  }, [url, type]);

  const fetchData = useCallback(async () => {
    if (!url) {
      setError("לא הוגדרה כתובת URL לנתונים");
      setIsLoading(false);
      return;
    }

    console.log("Fetching data from URL:", url);
    setIsLoading(true);

    try {
      // First try to get data from cache
      const cachedData = await fetchFromCache();
      if (cachedData) {
        console.log("Using cached data");
        setData(cachedData as HebrewData);
        setError(null);
        setIsLoading(false);
        return;
      }

      // If no valid cache, fetch from URL
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const origin = window.location.origin;
      console.log("Request origin:", origin);
      
      let fetchOptions: RequestInit = {
        signal: controller.signal,
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Origin': origin,
        },
        mode: 'cors'
      };
      // If location is provided, send as POST with body
      if (location && url.includes('zmanim')) {
        fetchOptions = {
          ...fetchOptions,
          method: 'POST',
          body: JSON.stringify({
            lat: location.lat,
            lng: location.lng,
            tzid: location.tzid || 'Asia/Jerusalem',
            date: new Date().toISOString().split('T')[0],
          })
        };
      }
      const response = await fetch(url, fetchOptions);
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const jsonData = await response.json();
      console.log("Received data:", jsonData);
      
      // Handle both array format and object format
      let formattedItems = [];
      let formattedData: HebrewData;
      
      if (Array.isArray(jsonData)) {
        formattedItems = jsonData.map(item => ({
          ...item,
          title: item.title || item.type || "",
          description: item.description || item.lesson || "",
        }));
        
        formattedData = {
          title: "",
          description: "",
          items: formattedItems
        };
      } else if (jsonData.items && Array.isArray(jsonData.items)) {
        formattedItems = jsonData.items.map(item => ({
          ...item,
          title: item.title || item.type || "",
          description: item.description || item.lesson || "",
        }));
        
        formattedData = {
          ...jsonData,
          items: formattedItems
        };
      } else {
        formattedData = {
          ...jsonData,
          items: []
        };
      }

      // Update cache with new data
      await updateCache(formattedData);
      
      setData(formattedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'שגיאה בטעינת הנתונים');
    } finally {
      setIsLoading(false);
    }
  }, [url, location, fetchFromCache, updateCache]);

  useEffect(() => {
    fetchData();

    // Set up refresh interval if specified
    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(
        fetchData, 
        refreshInterval * 60 * 1000 // Convert minutes to milliseconds
      );
      return () => clearInterval(interval);
    }
  }, [url, refreshInterval, fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};
